import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Stack, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ReadOnlyInput from "../ReadOnlyInput";
import Divider from "@mui/material/Divider";
import { useDispatch, useSelector } from "react-redux";
import { handleBack } from "../../redux/slices/addNewContentStepsSlice";
import { setComplianceChecked } from "../../redux/slices/contentSlice/contentForm/contentForm";
import ManifestFilesPreview from "./ManifestFilesPreview";
import ContentSuccessModal from "./ContentSuccessModal";
import SuccessModal from "../SuccessModal";
import SubmittedFilesPreview from "./SubmittedFilesPreview";
import { generateClient } from "aws-amplify/api";
import {
	createFeedback,
	createTimestamp,
	updateContent,
} from "../../graphql/mutations";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CirclePlus } from "lucide-react";
import IconButton from "@mui/material/IconButton";
import VideoTimestampRange from "./VideoTimestampRange";
import { setLoading } from "../../redux/slices/apiSlice";
import Popover from "@mui/material/Popover";
import TimestampPopover from "./TimestampPopover";
import { getProperties } from "aws-amplify/storage";
import { handleSuccessState } from "../../redux/slices/success";
import { success } from "../../theme/palette";
import Alert from "@mui/material/Alert";
import { WATCH, LISTEN, READ } from "../../constants";

const ManifestPreview = ({
	loading,
	contentValues,
	submit = false,
	localContentSource,
}) => {
	const submitSuccessfully = useSelector(
		(state) => state.contentForm.submittedSuccessfully
	);
	const successState = useSelector((state) => state.success.successState);
	const apiLoading = useSelector((state) => state?.api?.loading);
	const user = useSelector((state) => state.auth.user);
	const [timestamps, setTimestamps] = useState([]);
	const [feedback, setFeedBack] = useState("");
	const [updateLoading, setUpdateLoading] = useState(false);
	
	const dispatch = useDispatch();
	const client = generateClient();
	const params = useParams();
	const navigate = useNavigate();

	// Enhanced renderRatings with proper error handling
	const renderRatings = () => {
		try {
			if (!contentValues) {
				console.warn("ContentValues is undefined in renderRatings");
				return null;
			}

			return (
				<>
					{contentValues?.rating && (
						<Box>
							<Typography component="span">
								Viewership Rating :{" "}
							</Typography>
							<Typography color={"primary.light"} component="span">
								{contentValues.rating}
							</Typography>
						</Box>
					)}

					{contentValues?.type !== READ && contentValues?.type !== LISTEN && (
						<>
							{contentValues?.imdbRating && (
								<Box>
									<Typography component="span">
										IMDB Rating :{" "}
									</Typography>
									<Typography
										color={"primary.light"}
										component="span"
									>
										{contentValues.imdbRating}
									</Typography>
								</Box>
							)}
							{contentValues?.rottenRating && (
								<Box>
									<Typography component="span">
										Rotten Tomatoes Rating :{" "}
									</Typography>
									<Typography
										color={"primary.light"}
										component="span"
									>
										{contentValues.rottenRating}
									</Typography>
								</Box>
							)}
						</>
					)}
				</>
			);
		} catch (error) {
			console.error("Error in renderRatings:", error);
			toast.error("Error displaying ratings information");
			return null;
		}
	};

	// Enhanced renderTags with proper error handling and null checks
	const renderTags = () => {
		try {
			if (!contentValues) {
				console.warn("ContentValues is undefined in renderTags");
				return null;
			}

			return (
				<>
					{contentValues?.genre && Array.isArray(contentValues.genre) && (
						<Box>
							<Typography component="span">Genre : </Typography>
							<Typography color={"primary.light"} component="span">
								{contentValues.genre.join(", ")}
							</Typography>
						</Box>
					)}

					{contentValues?.type === READ ? (
						<Box>
							<Typography component="span">Author : </Typography>
							<Typography color={"primary.light"} component="span">
								{contentValues?.author || "N/A"}
							</Typography>
						</Box>
					) : contentValues?.type === LISTEN ? (
						<Box>
							<Typography component="span">Composer : </Typography>
							<Typography color={"primary.light"} component="span">
								{contentValues?.composer || "N/A"}
							</Typography>
						</Box>
					) : (
						<Box display={"flex"} gap={1}>
							<Typography component="div">
								Crew and Cast :{" "}
							</Typography>
							<Typography color={"primary.light"} component="div">
								{contentValues?.director && (
									<>
										Directed By : {contentValues.director}
										<br />
									</>
								)}
								<Typography
									component={"span"}
									color={"primary.light"}
								>
									Starring By :{" "}
								</Typography>
								{contentValues?.actors && Array.isArray(contentValues.actors) 
									? contentValues.actors.join(", ")
									: typeof contentValues?.actors === 'string'
									? contentValues.actors
									: "N/A"
								}
							</Typography>
						</Box>
					)}
				</>
			);
		} catch (error) {
			console.error("Error in renderTags:", error);
			toast.error("Error displaying tags information");
			return null;
		}
	};

	const handleApproveContent = async () => {
		try {
			if (!params?.id) {
				toast.error("Content ID is missing");
				return;
			}

			setUpdateLoading(true);
			console.log("Approving content with ID:", params.id);

			const response = await client.graphql({
				query: updateContent,
				variables: {
					input: {
						id: params.id,
						status: "Approved",
						releaseDate: new Date().toISOString(),
					},
				},
			});

			if (!response.data?.updateContent) {
				throw new Error("Failed to update content - no response data");
			}

			console.log("Content approved successfully:", response.data.updateContent);
			toast.success("Content has been approved successfully!");
			
			dispatch(
				handleSuccessState({
					success: true,
					message: "Content has been approved successfully",
					open: true,
				})
			);
			
			navigate("/content");
		} catch (error) {
			console.error("Error approving content:", error);
			
			let errorMessage = "Failed to approve content";
			if (error.errors && error.errors.length > 0) {
				errorMessage = error.errors.map(e => e.message).join(", ");
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
			dispatch(
				handleSuccessState({
					success: false,
					message: errorMessage,
					open: true,
				})
			);
		} finally {
			setUpdateLoading(false);
		}
	};

	// Enhanced handleFeedback with proper error handling
	const handleFeedback = async () => {
		try {
			if (!feedback.trim()) {
				toast.error("Please provide feedback before submitting");
				return;
			}

			if (!params?.id) {
				toast.error("Content ID is missing");
				return;
			}

			dispatch(setLoading(true));
			console.log("Submitting feedback for content ID:", params.id);

			// Create feedback
			const feedbackResponse = await client.graphql({
				query: createFeedback,
				variables: {
					input: {
						contentId: params.id,
						overallComment: feedback,
					},
				},
			});

			if (!feedbackResponse.data?.createFeedback) {
				throw new Error("Failed to create feedback");
			}

			console.log("Feedback created successfully:", feedbackResponse.data.createFeedback);

			// Update content status
			const updateResponse = await client.graphql({
				query: updateContent,
				variables: {
					input: {
						id: params.id,
						status: "Feedback",
					},
				},
			});

			if (!updateResponse.data?.updateContent) {
				throw new Error("Failed to update content status");
			}

			console.log("Content status updated successfully");

			// Create timestamps if any
			if (timestamps && timestamps.length > 0) {
				console.log(`Creating ${timestamps.length} timestamps...`);
				
				const timestampPromises = timestamps.map(async (stamp) => {
					if (!stamp.fromTime || !stamp.toTime || !stamp.comment) {
						console.warn("Skipping incomplete timestamp:", stamp);
						return null;
					}

					try {
						const timestampResponse = await client.graphql({
							query: createTimestamp,
							variables: {
								input: {
									feedbackId: feedbackResponse.data.createFeedback.id,
									fromTime: stamp.fromTime,
									toTime: stamp.toTime,
									comment: stamp.comment,
								},
							},
						});
						return timestampResponse.data?.createTimestamp;
					} catch (error) {
						console.error("Error creating timestamp:", error);
						throw error;
					}
				});

				const timestampResults = await Promise.all(timestampPromises);
				const successfulTimestamps = timestampResults.filter(result => result !== null);
				console.log(`${successfulTimestamps.length} timestamps created successfully`);
			}

			toast.success("Feedback submitted successfully!");
			dispatch(
				handleSuccessState({
					success: true,
					message: "Feedback has been submitted successfully",
					open: true,
				})
			);

			// Clear form
			setFeedBack("");
			setTimestamps([]);
			
			navigate("/content");
		} catch (error) {
			console.error("Error submitting feedback:", error);
			
			let errorMessage = "Failed to submit feedback";
			if (error.errors && error.errors.length > 0) {
				errorMessage = error.errors.map(e => e.message).join(", ");
			} else if (error.message) {
				errorMessage = error.message;
			}

			toast.error(errorMessage);
			dispatch(
				handleSuccessState({
					success: false,
					message: errorMessage,
					open: true,
				})
			);
		} finally {
			dispatch(setLoading(false));
		}
	};

	// Enhanced timestamp management
	const addTimestamps = () => {
		try {
			const newTimestamp = {
				id: Date.now(),
				fromTime: "",
				toTime: "",
				comment: "",
			};
			
			setTimestamps((prevState) => [...prevState, newTimestamp]);
			toast.success("Timestamp added");
		} catch (error) {
			console.error("Error adding timestamp:", error);
			toast.error("Error adding timestamp");
		}
	};

	const deleteTimestamp = (id) => {
		try {
			setTimestamps((prevState) =>
				prevState.filter((item) => item.id !== id)
			);
			toast.success("Timestamp removed");
		} catch (error) {
			console.error("Error deleting timestamp:", error);
			toast.error("Error deleting timestamp");
		}
	};

	// Enhanced preview items with error handling
	const getPreviewItems = () => {
		try {
			if (!contentValues) {
				console.warn("ContentValues is undefined in getPreviewItems");
				return [];
			}

			const previewItems = [
				{
					label: "Title :",
					value: contentValues?.title || "N/A",
				},
				{
					label: "Content Type :",
					value: contentValues?.type || "N/A",
				},
				{
					label: "Category :",
					value: contentValues?.category || "N/A",
				},
				{
					label: "Year Of Release :",
					value: contentValues?.year || "N/A",
				},
			];

			if (contentValues?.synopsis) {
				previewItems.push({
					label: "Description :",
					value: contentValues.synopsis,
				});
			}

			if (contentValues?.duration) {
				previewItems.push({
					label: "Duration :",
					value: `${contentValues.duration} mins`,
				});
			}

			return previewItems;
		} catch (error) {
			console.error("Error in getPreviewItems:", error);
			toast.error("Error loading preview items");
			return [];
		}
	};

	const previewItems = getPreviewItems();

	console.log("Timestamps:", timestamps);

	return (
		<>
			{contentValues?.status === "Feedback" && user?.role === "Admin" && (
				<Stack sx={{ width: "100%" }} my={2} spacing={2}>
					<Alert sx={{ fontSize: "20px" }} severity="info">
						This content has been sent for feedback.
					</Alert>
				</Stack>
			)}

			<Box
				display={"flex"}
				borderRadius={2}
				border={1}
				borderColor={"lightgrey"}
				margin={4}
				padding={2}
			>
				<Stack flexBasis={"80%"} paddingX={2} gap={2}>
					{previewItems?.map((item, index) => (
						<ReadOnlyInput
							key={index}
							label={item?.label}
							value={item?.value}
							containerProps={{
								sx: {
									display: "flex",
									gap: 2,
									alignItems: "center",
								},
							}}
							labelProps={{
								sx: {
									minWidth: "150px",
								},
							}}
						/>
					))}

					<ReadOnlyInput
						label={"Tags : "}
						value={renderTags()}
						containerProps={{
							sx: {
								display: "flex",
								gap: 2,
								alignItems: "center",
							},
						}}
						labelProps={{
							sx: {
								minWidth: "150px !important",
							},
						}}
					/>
					
					{contentValues?.rating && (
						<ReadOnlyInput
							label={"Ratings : "}
							value={renderRatings()}
							containerProps={{
								sx: {
									display: "flex",
									gap: 2,
									alignItems: "center",
								},
							}}
							labelProps={{
								sx: {
									minWidth: "150px !important",
								},
							}}
						/>
					)}

					{submit && (
						<Box display={"flex"} justifyContent={"space-between"}>
							<Button
								onClick={() => {
									try {
										dispatch(setComplianceChecked(false));
										dispatch(handleBack());
									} catch (error) {
										console.error("Error navigating back:", error);
										toast.error("Error navigating back");
									}
								}}
								disabled={loading}
							>
								Back
							</Button>
							<LoadingButton
								loading={loading}
								type="submit"
								variant="contained"
								disabled={!contentValues}
							>
								Submit
							</LoadingButton>
						</Box>
					)}

					{user?.role === "Admin" && (
						<>
							{contentValues?.status === "Pending" ? (
								<>
									{contentValues?.type === WATCH && (
										<Box>
											<Stack
												direction={"row"}
												alignItems={"center"}
												gap={2}
											>
												<Typography
													component={"span"}
													variant="h6"
												>
													Add Timestamps
												</Typography>
												<IconButton
													onClick={addTimestamps}
													disabled={apiLoading}
												>
													<CirclePlus />
												</IconButton>
											</Stack>
											{timestamps?.map((stamp, index) => (
												<VideoTimestampRange
													key={stamp.id}
													setTimestamps={setTimestamps}
													index={index}
													deleteTimestamp={deleteTimestamp}
													timestamp={stamp}
												/>
											))}
										</Box>
									)}
									<Typography component={"span"} variant="h6">
										Add Overall Feedback Here
									</Typography>
									<TextField
										placeholder="Add your overall feedback for the content here..."
										multiline
										rows={5}
										value={feedback}
										onChange={(e) => setFeedBack(e.target.value)}
										fullWidth
										disabled={apiLoading}
									/>
									<Stack direction={"row"} gap={2}>
										<LoadingButton
											loading={apiLoading}
											variant="outlined"
											onClick={handleFeedback}
											disabled={!feedback.trim()}
										>
											Send Feedback
										</LoadingButton>
										<LoadingButton
											loading={updateLoading}
											sx={{
												backgroundColor: "primary.dark",
												color: "secondary.main",
											}}
											variant="contained"
											onClick={handleApproveContent}
										>
											Approve
										</LoadingButton>
									</Stack>
								</>
							) : null}
						</>
					)}
					
					{contentValues?.feedbacks &&
						contentValues?.feedbacks?.items?.length > 0 &&
						contentValues?.feedbacks?.items?.map((feedback, feedbackIndex) => (
							<Box key={feedbackIndex}>
								<Divider
									sx={{
										borderWidth: "0.5px",
										my: 2,
										mr: -2,
										ml: -4,
									}}
								/>
								<Typography variant="h5">Feedback </Typography>

								<Typography>
									Comments : {feedback?.overallComment || "No comments"}
								</Typography>

								{feedback?.timestamps?.items?.length > 0 &&
									feedback?.timestamps?.items?.map(
										(timestamp, timestampIndex) => (
											<Stack
												key={timestampIndex}
												direction={"row"}
												alignItems={"center"}
												gap={3}
											>
												<Typography
													component={"span"}
													variant="h6"
												>
													Timestamp :
												</Typography>
												<Typography component={"span"}>
													{timestamp?.fromTime || "00:00"} to{" "}
													{timestamp?.toTime || "00:00"}
												</Typography>
												<Typography component={"span"}>
													{timestamp?.comment || "No comment"}
												</Typography>
											</Stack>
										)
									)}
							</Box>
						))}
				</Stack>
				<Divider sx={{ borderWidth: "0.5px", my: -2 }} />

				<Stack paddingX={2} flexBasis={"20%"}>
					<Typography
						mb={2}
						textAlign={"center"}
						variant="h6"
						fontWeight={500}
					>
						{contentValues?.title || "Content Preview"}
					</Typography>
					{submit ? (
						<ManifestFilesPreview
							localContentSource={localContentSource}
						/>
					) : (
						<SubmittedFilesPreview content={contentValues} />
					)}
				</Stack>
				
				<SuccessModal />
			</Box>
		</>
	);
};

export default ManifestPreview;